import type {ContextCrumb, ContextRegionActions, CrumbOptions} from '@github-ui/global-navigation-types'
import {Link} from '@github-ui/react-router'
import {useSyncedState} from '@github-ui/use-synced-state'
import {Breadcrumbs} from '@primer/react'
import {clsx} from 'clsx'
import React, {useCallback, useEffect, useImperativeHandle, useRef} from 'react'

import {clearRegisteredContextRegion, getContextRegion, registerContextRegionHandler} from '../../global-navigation'
import {useTopNavConfig} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {NavOcticon} from '../../utils/octicons'
import {RepoPicker} from './RepoPicker'
import styles from './styles.module.css'

export function toContextCrumb(crumb: CrumbOptions): ContextCrumb {
  return {
    crumb_type: 'unknown',
    label: crumb.label,
    is_root: false,
    href: crumb.href,
  }
}

// Stable empty reference for when no crumbs are provided, so prop-syncing isn't churned by a fresh
// array each render.
const EMPTY_CRUMBS: ContextCrumb[] = []

export interface ContextRegionProps {
  /** Array of breadcrumb items for the context region */
  crumbs?: ContextCrumb[]

  /** Whether the global navigation payload includes a logged-in viewer */
  viewerIsLoggedIn?: boolean

  /** Callback fired when the crumbs are updated */
  onUpdateCrumbs?: (crumbs: ContextCrumb[]) => void

  ref?: React.Ref<ContextRegionActions>
}

export function useRegisterContextRegion(): Partial<ContextRegionProps & React.RefAttributes<ContextRegionActions>> {
  const ref = useRef<ContextRegionActions | null>(null)

  useEffect(() => {
    const {current} = ref

    if (current && getContextRegion({source: 'react', silent: true}) !== current) {
      registerContextRegionHandler(current)
    }

    return () => {
      if (getContextRegion({source: 'react', silent: true}) === current) {
        clearRegisteredContextRegion()
      }
    }
  }, [])

  return {ref}
}

export function ContextRegion({ref, viewerIsLoggedIn = false, ...props}: ContextRegionProps) {
  const {sendItemClick} = useGlobalNavAnalytics('CONTEXT_REGION')
  const {isSearchPage} = useTopNavConfig()
  const {onUpdateCrumbs} = props

  // Keep `crumbs` in sync with the `crumbs` prop during render rather than in a post-paint effect,
  // so a breadcrumb change doesn't flash a stale frame before an effect corrects it. Crumbs are
  // also updated imperatively (pushCrumb/popCrumb/etc.), so when the prop is absent we disable
  // prop-syncing to preserve those imperative updates — matching the previous `if (props.crumbs)`
  // guard.
  const [crumbs, setCrumbs] = useSyncedState<ContextCrumb[]>(props.crumbs ?? EMPTY_CRUMBS, {
    isPropUpdateDisabled: !props.crumbs,
  })

  const updateCrumbs = useCallback(
    (updateFn: ContextCrumb[] | ((oldCrumbs: ContextCrumb[]) => ContextCrumb[])) => {
      setCrumbs(prevCrumbs => {
        const newCrumbs = typeof updateFn === 'function' ? updateFn(prevCrumbs) : updateFn
        onUpdateCrumbs?.(newCrumbs)
        return newCrumbs
      })
    },
    [onUpdateCrumbs, setCrumbs],
  )

  useImperativeHandle(ref, () => {
    return {
      pushCrumb: (crumb: CrumbOptions) => {
        updateCrumbs(prevCrumbs => [...prevCrumbs, toContextCrumb(crumb)])
      },
      popCrumb: () => {
        updateCrumbs(prevCrumbs => prevCrumbs.slice(0, -1))
      },
      replaceCrumbs: (newCrumbs: CrumbOptions[]) => {
        updateCrumbs(newCrumbs.map(toContextCrumb))
      },
      renameCurrentCrumb: (label: string) => {
        updateCrumbs(prevCrumbs => {
          const lastCrumb = prevCrumbs[prevCrumbs.length - 1]

          if (!lastCrumb) {
            return prevCrumbs
          }

          const oldCrumbs = prevCrumbs.slice(0, -1)
          return [...oldCrumbs, {...lastCrumb, label}]
        })
      },
      replaceCurrentCrumb: (crumb: CrumbOptions) => {
        updateCrumbs(prevCrumbs => {
          if (prevCrumbs.length === 0) {
            return prevCrumbs
          }
          const updatedCrumbs = [...prevCrumbs]
          const lastIndex = updatedCrumbs.length - 1
          updatedCrumbs[lastIndex] = toContextCrumb(crumb)
          return updatedCrumbs
        })
      },
    }
  }, [updateCrumbs])

  if (!crumbs || crumbs.length === 0 || isSearchPage) {
    return null
  }

  const lastCrumbIsRepo = crumbs[crumbs.length - 1]?.crumb_type === 'repository'

  const repositoryPickerRepoCrumb =
    viewerIsLoggedIn && lastCrumbIsRepo ? crumbs.find(crumb => crumb.crumb_type === 'repository') : undefined

  // find this repo's owner crumb so we can find other repos under the same owner
  const ownerCrumb =
    lastCrumbIsRepo && repositoryPickerRepoCrumb ? crumbs[crumbs.indexOf(repositoryPickerRepoCrumb) - 1] : undefined

  return (
    <Breadcrumbs className={styles.contextRegion} overflow="menu">
      {crumbs.map((crumb, index) => {
        if (ownerCrumb && repositoryPickerRepoCrumb && crumb.crumb_type === 'repository') {
          // when the repo picker experiment is enabled, we don't show the repository crumb in the context region
          return (
            <RepoPicker
              // A stable key keeps the picker trigger, and keyboard focus on it, across repository changes.
              key="repository-picker"
              repositoryCrumb={repositoryPickerRepoCrumb}
              ownerCrumb={ownerCrumb}
              onCrumbClick={() => {
                sendItemClick('CONTEXT_REGION_CRUMB', `position:${index + 1}`)
              }}
            />
          )
        }

        const href = crumb.href || crumb.path
        const isLast = index === crumbs.length - 1
        return (
          <Breadcrumbs.Item
            key={`${href || crumb.label}`}
            as={href ? Link : 'span'}
            to={href}
            className={clsx({
              [styles.contextCrumb]: true,
              [styles.contextCrumbStatic]: !href,
            })}
            onClick={() => {
              sendItemClick('CONTEXT_REGION_CRUMB', `position:${index + 1}`)
            }}
          >
            <span className={isLast ? styles.contextCrumbLast : ''}>{crumb.label}</span>
            <NavOcticon name={crumb.octicon} size={12} className={styles.trailingIcon} />
          </Breadcrumbs.Item>
        )
      })}
    </Breadcrumbs>
  )
}
